import React from 'react';
import PropTypes from 'prop-types';
import { getContext } from '@fiverr-private/fiverr_context';
import { I18n } from '@fiverr-private/i18n-react';
import pathfinder from '@fiverr-private/pathfinder';
import { EducationTooltip } from '@fiverr-private/orca';
import { Domingo } from '@fiverr-private/domingo';
import * as Links from '../Links';
import Link from '../Links/Link';
import { pathfinderQueryEnrichment } from '../../utils/pathfinderQueryEnrichment';
import EducationTooltipTitle from '../EducationTooltipTitle';
import SellerPlusLabel from '../SellerPlusLabel';
import { TOP_BAR_SOURCE } from '../../constants';
import SellerAchievementsLink from '../Seller/Links/SellerOverview';

export const bigQuerySeller = (id, type) => ({
    group: 'header_clicks',
    type: `clicked_header.${type}`,
    user: {
        id,
        navigation_mode: 'selling',
    },
});

export const bigQuerySellerKickstartProgram = (id, type, isKickstart = false) => ({
    group: 'header_clicks',
    type: `clicked_header.${type}`,
    user: {
        id,
        navigation_mode: 'selling',
    },
    program: {
        ...(isKickstart ? { type: 'kickstart_basic' } : {}),
        category: isKickstart ? 'kickstart' : 'seller_plus',
    },
});

export const SellerDashboardLink = ({ sellerId, sellerUsername, className }) => {
    const path = pathfinder('user_seller_dashboard_index', {
        user_id: sellerUsername,
    });

    return (
        <Links.UserDashboardLink
            bigQueryData={bigQuerySeller(sellerId, 'my_dashboard')}
            href={path}
            className={className}
        />
    );
};

SellerDashboardLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
    sellerUsername: PropTypes.string.isRequired,
    className: PropTypes.string,
};

export const SellerOrdersLink = ({ sellerId, sellerUsername }) => (
    <Links.UserOrdersLink username={sellerUsername} bigQueryData={bigQuerySeller(sellerId, 'selling_orders')} />
);

SellerOrdersLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
    sellerUsername: PropTypes.string.isRequired,
};

export const SellerBriefsLink = ({ sellerId }) => (
    <Links.UserBriefsLink bigQueryData={bigQuerySeller(sellerId, 'selling_orders')} />
);

SellerBriefsLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
};

export const SellerGigsLink = ({ sellerId, sellerUsername }) => (
    <Links.UserGigsLink username={sellerUsername} bigQueryData={bigQuerySeller(sellerId, 'selling_gigs')} />
);

SellerGigsLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
    sellerUsername: PropTypes.string.isRequired,
};

export const SellerManageCatalogLink = ({ sellerId }) => (
    <Links.UserManageCatalogLink bigQueryData={bigQuerySeller(sellerId, 'selling_manage_catalog')} />
);

SellerManageCatalogLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
};

export const SellerAnalyticsLink = ({ sellerId, sellerUsername }) => (
    <Links.UserAnalyticsLink username={sellerUsername} bigQueryData={bigQuerySeller(sellerId, 'selling_analytics')} />
);

SellerAnalyticsLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
    sellerUsername: PropTypes.string.isRequired,
};

export const SellerAnalyticsOverviewLink = ({ sellerId, sellerUsername }) => (
    <Links.UserAnalyticsOverviewLink
        username={sellerUsername}
        bigQueryData={bigQuerySeller(sellerId, 'selling_analytics')}
    />
);

SellerAnalyticsOverviewLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
    sellerUsername: PropTypes.string.isRequired,
};

export const SellerAnalyticsGigConversionLink = ({ sellerId, sellerUsername, isLocked = false }) => (
    <Links.UserAnalyticsGigConversionLink
        username={sellerUsername}
        isLocked={isLocked}
        bigQueryData={bigQuerySeller(sellerId, 'selling_analytics_gig_conversion')}
    />
);

SellerAnalyticsGigConversionLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
    sellerUsername: PropTypes.string.isRequired,
    isLocked: PropTypes.bool,
};

export const SellerAnalyticsOrdersBreakdownLink = ({ sellerId, sellerUsername, isLocked = false }) => (
    <Links.UserAnalyticsOrdersBreakdownLink
        username={sellerUsername}
        isLocked={isLocked}
        bigQueryData={bigQuerySeller(sellerId, 'selling_analytics_attribution')}
    />
);

SellerAnalyticsOrdersBreakdownLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
    sellerUsername: PropTypes.string.isRequired,
    isLocked: PropTypes.bool,
};

export const SellerAnalyticsTopKeywordsLink = ({ sellerId, sellerUsername, isLocked = false }) => (
    <Links.UserAnalyticsTopKeywordsLink
        username={sellerUsername}
        isLocked={isLocked}
        bigQueryData={bigQuerySeller(sellerId, 'selling_analytics_keywords')}
    />
);

SellerAnalyticsTopKeywordsLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
    sellerUsername: PropTypes.string.isRequired,
    isLocked: PropTypes.bool,
};

export const SellerAnalyticsKeywordResearchLink = ({
    sellerId,
    sellerUsername,
    isLocked = false,
    isKickstart = false,
}) => (
    <Links.UserAnalyticsKeywordResearchLink
        username={sellerUsername}
        isLocked={isLocked}
        bigQueryData={bigQuerySellerKickstartProgram(sellerId, 'selling_analytics_keyword_research', isKickstart)}
    />
);

SellerAnalyticsKeywordResearchLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
    sellerUsername: PropTypes.string.isRequired,
    isLocked: PropTypes.bool,
    isKickstart: PropTypes.bool,
};

export const SellerAnalyticsLoyaltyLink = ({ sellerId, sellerUsername }) => (
    <Links.UserAnalyticsLoyaltyLink
        username={sellerUsername}
        bigQueryData={bigQuerySeller(sellerId, 'selling_analytics_loyalty')}
    />
);

SellerAnalyticsLoyaltyLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
    sellerUsername: PropTypes.string.isRequired,
};

export const SellerPromotedGigsLink = ({ sellerId, sellerUsername, ...props }) => {
    const path = pathfinder('user_promoted_gigs', {
        user_id: sellerUsername,
    });

    return (
        <Link {...props} href={path} bigQueryData={bigQuerySeller(sellerId, 'promoted_gigs')} className="nav-link">
            {(i18n) => i18n.t('header.promoted_gigs')}
        </Link>
    );
};

SellerPromotedGigsLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
    sellerUsername: PropTypes.string.isRequired,
};

export const SellerPlusEligibleLink = ({ ...props }) => {
    const { userId, isPro } = getContext();
    const path = pathfinder(
        'seller_plus_program_page_seller_plus',
        null,
        pathfinderQueryEnrichment({ context: 'invitation' })
    );

    const mpData = {
        eventName: 'seller plus - clicked header',
        eventData: {
            'Seller ID': userId,
            'Is Pro Seller': isPro,
        },
    };

    return (
        <Link
            {...props}
            mpData={mpData}
            bigQueryData={bigQuerySeller(userId, 'seller_plus')}
            href={path}
            className="nav-link beta"
        >
            <SellerPlusLabel />
        </Link>
    );
};

export const SellerPlusLink = ({ labelKey = 'header.program_page', ...props }) => {
    const { userId, isPro } = getContext();
    const path = pathfinder('seller_plus_program_page_seller_plus', null, pathfinderQueryEnrichment());

    const mpData = {
        eventName: 'seller plus - clicked header',
        eventData: {
            'Seller ID': userId,
            'Is Pro Seller': isPro,
        },
    };

    return (
        <Link
            {...props}
            mpData={mpData}
            bigQueryData={bigQuerySeller(userId, 'seller_plus')}
            href={path}
            className="nav-link beta"
        >
            {(i18n) => i18n.t(labelKey)}
        </Link>
    );
};

SellerPlusLink.propTypes = {
    labelKey: PropTypes.string,
};

export const KickstartProgramPageLink = ({ labelKey, ...props }) => {
    const { userId, isPro } = getContext();
    const path = pathfinder('kickstart_program_page', null, pathfinderQueryEnrichment());

    const mpData = {
        eventName: 'kickstart program page - clicked header',
        eventData: {
            'Seller ID': userId,
            'Is Pro Seller': isPro,
        },
    };

    return (
        <Link
            {...props}
            mpData={mpData}
            bigQueryData={bigQuerySellerKickstartProgram(userId, 'kickstart', true)}
            href={path}
            className="nav-link beta"
        >
            {(i18n) => i18n.t(labelKey)}
        </Link>
    );
};

KickstartProgramPageLink.propTypes = {
    labelKey: PropTypes.string,
};

export const SellerEarningsLink = ({ sellerId, sellerUsername }) => (
    <Links.UserEarningsLink username={sellerUsername} bigQueryData={bigQuerySeller(sellerId, 'selling_earning')} />
);

SellerEarningsLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
    sellerUsername: PropTypes.string.isRequired,
};

export const SellerMarketingToolsLink = ({ sellerId, ...props }) => {
    const path = pathfinder('seller_plus_marketing_tools_page', null, pathfinderQueryEnrichment());

    return (
        <Link {...props} href={path} bigQueryData={bigQuerySeller(sellerId, 'marketing_tools')} className="nav-link">
            <I18n k="header.marketing_tools" />
        </Link>
    );
};

SellerMarketingToolsLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
    ...Link.propTypes,
};

export const SellerSPForumLink = ({ sellerId, ...props }) => {
    const path = 'https://community.fiverr.com/home/clubs/seller-plus-9ga3q/overview';

    return (
        <Link
            {...props}
            href={path}
            bigQueryData={bigQuerySeller(sellerId, 'seller_plus_forum')}
            target="_blank"
            rel="noopener noreferrer"
            className="nav-link"
        >
            <I18n k="header.seller_plus_forum" />
        </Link>
    );
};

SellerSPForumLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
    ...Link.propTypes,
};

export const SellerEventsLink = ({ sellerId }) => (
    <Links.EventsLink bigQueryData={bigQuerySeller(sellerId, 'community_events')} />
);

SellerEventsLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
};

export const SellerBlogLink = ({ sellerId }) => (
    <Links.BlogLink
        bigQueryData={bigQuerySeller(sellerId, 'community_blogs')}
        labelKey="header.link_label.fiverr_blog"
    />
);

SellerBlogLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
};

export const SellerForumLink = ({ sellerId }) => (
    <Links.ForumLink
        bigQueryData={bigQuerySeller(sellerId, 'community_forum')}
        labelKey="header.link_label.fiverr_forum"
    />
);

SellerForumLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
};

export const SellerCommunityStandardsLink = ({ sellerId }) => (
    <Links.CommunityStandardsLink bigQueryData={bigQuerySeller(sellerId, 'community_standards')} />
);

SellerCommunityStandardsLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
};

export const SellerPodcastLink = ({ sellerId }) => (
    <Links.PodcastLink bigQueryData={bigQuerySeller(sellerId, 'community_podcast')} />
);

SellerPodcastLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
};

export const SellerContactsLink = ({ sellerId }) => (
    <Links.ContactsLink bigQueryData={bigQuerySeller(sellerId, 'selling_contact')} />
);

SellerContactsLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
};

export const SellerProfileLink = ({ sellerId, sellerUsername }) => (
    <Links.ProfileLink username={sellerUsername} bigQueryData={bigQuerySeller(sellerId, 'my_profile')} />
);

SellerProfileLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
    sellerUsername: PropTypes.string.isRequired,
};

export const SellerReferalLink = ({ sellerId }) => (
    <Links.ReferralLink bigQueryData={bigQuerySeller(sellerId, 'refer_a_friend')} />
);

SellerReferalLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
};

export const SellerAccountSettingsLink = ({ sellerId }) => (
    <Links.UserAccountSettingsLink bigQueryData={bigQuerySeller(sellerId, 'settings')} />
);

SellerAccountSettingsLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
};

export const SellerBillingLink = ({ sellerId }) => (
    <Links.BillingLink isSeller bigQueryData={bigQuerySeller(sellerId, 'billing')} />
);

SellerBillingLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
};

export const SellerHelpLink = ({ sellerId }) => (
    <Links.HelpLink userType="buyer_seller" bigQueryData={bigQuerySeller(sellerId, 'help')} />
);

SellerHelpLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
};

export const SellerLevelOverviewLink = ({ sellerId }) => (
    <SellerAchievementsLink userType="buyer_seller" bigQueryData={bigQuerySeller(sellerId, 'achievements')} />
);

SellerLevelOverviewLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
};

export const SellerBusinessLink = ({ sellerId }) => (
    <EducationTooltip
        title={<EducationTooltipTitle tagKey={'header.new'} titleKey={'header.seller_business_education.title'} />}
        body={<I18n k="header.seller_business_education.body_pro" />}
        closeTitle={<I18n k="header.seller_business_education.close" />}
        position={EducationTooltip.POSITIONS.BOTTOM}
        tooltipStorageKey="header_seller_business_link_education"
        appendToBody={true}
    >
        <Link
            href={proFreelancersLink()}
            className="nav-link"
            bigQueryData={bigQuerySeller(sellerId, 'selling_fiverr_business')}
            target="_blank"
            rel="noopener noreferrer"
        >
            {<I18n k="header.fiverr_pro" />}
        </Link>
    </EducationTooltip>
);

const proFreelancersLink = () =>
    Domingo(getContext().url)
        .removePath()
        .removePro()
        .path(
            'content_pages_page',
            { page_slug: 'pro-freelancers' },
            {
                query: { source: TOP_BAR_SOURCE },
            }
        ).url;

SellerBusinessLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
};

export const SellerResellingDashboardLink = ({ sellerId }) => {
    const path = pathfinder('reselling_seller_dashboard');

    return (
        <Links.ResellingSellerDashboardLink
            href={path}
            bigQueryData={bigQuerySeller(sellerId, 'reselling_seller_dashboard')}
        />
    );
};

SellerResellingDashboardLink.propTypes = {
    sellerId: PropTypes.number.isRequired,
};
